// src/backendServices/liveStreamManagment/regie_autosave.ts
//
// Sauvegarde automatique du mediaboard d'une chaîne sur le serveur.
//
// Une seule RegieConfig par chaîne, nommée AUTOSAVE_NAME, réécrite (PUT) après
// chaque modification des slots image / son / PiP / jingle. Les fichiers
// (images, sons, jingles) sont téléversés sous cette config, une seule fois par
// version de média. Au retour sur une chaîne dont IndexedDB n'a plus les
// fichiers (autre poste, cache vidé), MediaStreamContext les recharge d'ici.
//
// Rien n'est écrit avant que la restauration de la chaîne ne soit terminée
// (markRestored) : sinon un poste neuf écraserait la sauvegarde par des défauts.

import { API_URLS } from '../urls';
import { isStreamedMediaUrl } from '../../dataTypes/liveStreamManagment/utils_videoSource';
import { FileUploaderWithStore } from '../fileManagment/uploadWithStore';
import { storeMediaBlob } from './local_media_store';
import { getRegieConfigs, downloadRegieConfigMedia, RegieMediaType } from './api_regieConfig';

export const AUTOSAVE_NAME = '__auto__';

const DEBOUNCE_MS = 2000;

export interface AutosaveSnapshot {
  image : any[];   // ImageSlot[]
  son   : any[];   // SoundSlot[]
  video : any[];   // JingleSlot[]
  pip   : any[];   // VideoOverlaySlot[]
  deck  : any[] | null;   // DeckEntry[] — emplacements du deck (voir deck_persistence.ts)
  keymap: any | null;     // DeckKeyMap — raccourcis de cette chaîne
  deckSettings: Record<string, any> | null;   // effets audio, réglages vidéo par source (voir deck_persistence.ts)
}

// Famille de slots → type de média côté serveur (les jingles sont rangés en « video »).
const MEDIA_FAMILIES: Array<{ key: 'image' | 'son' | 'video'; mediaType: Exclude<RegieMediaType, 'scene'> }> = [
  { key: 'image', mediaType: 'image' },
  { key: 'son',   mediaType: 'sound' },
  { key: 'video', mediaType: 'video' },
];

const restored   = new Set<string>();
const configIds  = new Map<string, number>();
const timers     = new Map<string, ReturnType<typeof setTimeout>>();
const running    = new Set<string>();
const dirty      = new Set<string>();
const getters    = new Map<string, () => AutosaveSnapshot>();

// ── Mémoire des fichiers déjà envoyés (par poste) ─────────────────────────────

const uploadedKey = (uuid: string) => `regie_autosave_uploaded:${uuid}`;

function readUploaded(uuid: string): Record<string, string> {
  try { return JSON.parse(localStorage.getItem(uploadedKey(uuid)) || '{}'); } catch { return {}; }
}

function writeUploaded(uuid: string, map: Record<string, string>): void {
  try { localStorage.setItem(uploadedKey(uuid), JSON.stringify(map)); } catch {}
}

export function markUploaded(uuid: string, mediaType: string, slotId: string, version: string): void {
  const m = readUploaded(uuid);
  m[`${mediaType}_${slotId}`] = version;
  writeUploaded(uuid, m);
}

// ── Réseau ────────────────────────────────────────────────────────────────────

async function findConfigId(uuid: string): Promise<number | null> {
  const cached = configIds.get(uuid);
  if (cached) return cached;
  const all   = await getRegieConfigs(uuid);
  const found = all.find(c => c.name === AUTOSAVE_NAME);
  if (found) configIds.set(uuid, found.id);
  return found?.id ?? null;
}

/** Sauvegarde automatique de la chaîne, ou null si elle n'existe pas encore. */
export async function fetchAutosave(uuid: string): Promise<{ id: number; data: any } | null> {
  const all   = await getRegieConfigs(uuid);
  const found = all.find(c => c.name === AUTOSAVE_NAME);
  if (!found) return null;
  configIds.set(uuid, found.id);
  return { id: found.id, data: (found.config as any)?.data ?? {} };
}

/** Télécharge un média de la sauvegarde et le range dans IndexedDB. */
export async function downloadAutosaveMedia(
  regieConfigId : number,
  mediaType     : Exclude<RegieMediaType, 'scene'>,
  slotId        : string,
  originalName? : string,
): Promise<{ url: string; instantMediaId: string } | null> {
  const prefix = `${mediaType}_${slotId}_slot`;
  try {
    const blob   = await downloadRegieConfigMedia(regieConfigId, prefix);
    const stored = await storeMediaBlob(blob, originalName || prefix, mediaType, slotId, 'slot');
    return { url: stored.objectUrl, instantMediaId: stored.mediaId };
  } catch {
    return null;   // pas de fichier côté serveur : on ne touche à rien
  }
}

async function uploadBlob(
  regieConfigId : number,
  mediaType     : Exclude<RegieMediaType, 'scene'>,
  slotId        : string,
  blob          : Blob,
  originalName? : string,
): Promise<boolean> {
  try {
    // Blob sans type (réponse serveur sans Content-Type) : l'extension du nom d'origine, sinon « .bin ».
    const ext      = blob.type ? blob.type.split('/')[1]?.split(';')[0] ?? 'bin' : (originalName?.split('.').pop()?.toLowerCase() || 'bin');
    const filename = `${mediaType}_${slotId}_slot.${ext}`;
    const file     = new File([blob], filename, { type: blob.type });
    const metadata = { regieConfigId, mediaType, slotId, role: 'slot', filename, fileType: '' };

    const uploader = new FileUploaderWithStore(file, {
      uploadChunkUrl   : API_URLS.uploadRegieMediaChunk,
      finalizeUploadUrl: API_URLS.finalizeRegieMediaUpload,
      chunkSize        : 5 * 1024 * 1024,
      maxRetries       : 3,
      enableResume     : false,   // même nom de fichier à chaque version : jamais de reprise
      entityId         : regieConfigId,
      entityType       : 'regie_media',
      autoStart        : false,
      metadata,
    } as any);
    await uploader.start(metadata as any);
    return true;
  } catch (e) {
    console.warn(`[RegieAutosave] Échec upload ${mediaType}/${slotId}:`, e);
    return false;
  }
}

function stripSlot(s: any): any {
  const { url, instantMediaId, ...rest } = s;
  // Vidéo HLS du compte : l'url voyage telle quelle, aucun fichier à reprendre.
  if (isStreamedMediaUrl(url)) return { ...rest, url, hasMedia: false };
  return { ...rest, hasMedia: !!url };
}

function buildPayload(uuid: string, snap: AutosaveSnapshot) {
  return {
    name       : AUTOSAVE_NAME,
    channelUuid: uuid,
    data       : {
      image: { slots: snap.image.map(stripSlot) },
      son  : { slots: snap.son.map(stripSlot) },
      video: { slots: snap.video.map(stripSlot) },
      // Absents tant que ce poste n'a rien enregistré : jamais de valeur vide qui
      // effacerait celle du serveur.
      ...(snap.deck   ? { deck  : { entries: snap.deck } } : {}),
      ...(snap.keymap ? { keymap: snap.keymap }            : {}),
      ...(snap.deckSettings ? { deckSettings: snap.deckSettings } : {}),
    },
  };
}

async function flush(uuid: string): Promise<void> {
  const getSnapshot = getters.get(uuid);
  if (!getSnapshot) return;
  const snap    = getSnapshot();
  const payload = buildPayload(uuid, snap);

  let id = await findConfigId(uuid);
  if (id === null) {
    const res = await fetch(API_URLS.saveRegieConfig, {
      method : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body   : JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`création ${res.status}`);
    id = (await res.json()).id as number;
    configIds.set(uuid, id);
  }

  // Fichiers d'abord, JSON ensuite : hasMedia ne promet jamais un fichier absent.
  const uploaded = readUploaded(uuid);
  for (const { key, mediaType } of MEDIA_FAMILIES) {
    for (const slot of snap[key]) {
      const k       = `${mediaType}_${slot.id}`;
      const version = slot.instantMediaId || slot.url;
      if (!slot.url || isStreamedMediaUrl(slot.url)) { delete uploaded[k]; continue; }
      if (uploaded[k] === version) continue;
      try {
        const blob = await (await fetch(slot.url)).blob();
        if (await uploadBlob(id, mediaType, slot.id, blob, slot.originalName)) uploaded[k] = version;
      } catch {
        // blob: URL morte et fichier absent d'IndexedDB : rien à sauver ici
      }
    }
  }
  writeUploaded(uuid, uploaded);

  const res = await fetch(API_URLS.updateRegieConfig.replace('{id}', `${id}`), {
    method : 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body   : JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`mise à jour ${res.status}`);
}

async function run(uuid: string): Promise<void> {
  if (running.has(uuid)) { dirty.add(uuid); return; }
  running.add(uuid);
  try {
    do {
      dirty.delete(uuid);
      await flush(uuid);
    } while (dirty.has(uuid));
  } catch (e) {
    console.warn('[RegieAutosave] Sauvegarde échouée :', e);
  } finally {
    running.delete(uuid);
  }
}

// ── API publique ──────────────────────────────────────────────────────────────

/** À appeler après chaque écriture du mediaboard d'une chaîne. */
export function scheduleAutosave(uuid: string, getSnapshot: () => AutosaveSnapshot): void {
  getters.set(uuid, getSnapshot);
  if (!restored.has(uuid)) { dirty.add(uuid); return; }   // rejoué par markRestored
  const t = timers.get(uuid);
  if (t) clearTimeout(t);
  timers.set(uuid, setTimeout(() => { timers.delete(uuid); void run(uuid); }, DEBOUNCE_MS));
}

/** La restauration depuis le serveur est terminée : les sauvegardes peuvent partir. */
export function markRestored(uuid: string): void {
  restored.add(uuid);
  const getSnapshot = getters.get(uuid);
  if (getSnapshot) scheduleAutosave(uuid, getSnapshot);
}
